/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import type { Styles } from 'react-jss';
import hoistNonReactStatics from 'hoist-non-react-statics';
import type { ITheme } from '@bingads-webui-react/with-default-styles';
import type { UAPClasses } from './@types/component-types';

const emptyTheme = { components: {}, core: {} };

export type UAPLTheme = ITheme;

export type WithThemedStylesOptions = {
  prefixClassName?: string;
  useRawStyles?: boolean;
  componentName?: string;
};

type ThemedComponentProps = {
  theme?: UAPLTheme;
};

type InjectedStyleProps = {
  classes: UAPClasses;
  themedStyles?: Styles;
};

type RenderableComponent<P> = ((props: P) => React.ReactElement | null) & {
  displayName?: string;
};

const getDisplayName = <P extends object>(Component: RenderableComponent<P>) =>
  Component.displayName || Component.name || 'Component';

// the purpose of the hoc is to deouple the wrapped components with a specific theme provider,
// to make the library as independent as possible

// use an external theme to decouple the wrapped components with a specific theme provider

export function withThemedStyles<P extends object>(
  WrappedComponent: RenderableComponent<P>,
  getStyles: (components: object, core: object) => Styles,
  options: WithThemedStylesOptions
) {
  const {
    prefixClassName, // the prefix of the class names generated by react-jss
    useRawStyles, // also pass down the themed styles to the wrapped component, which may be used in Fluent UI components
    componentName, // the description name of WrappedComponent
  } = options || {};


  const HOC = (props: Omit<P, keyof InjectedStyleProps> & ThemedComponentProps) => {
    // the theme is passed as a prop to the wrapped component, instead of getting if from some theme provider
    const { theme } = props;
    const { components, core } = theme || emptyTheme;
    // handle theme by ourselves instead of using the react-jss theming
    const themedStyles = useMemo(() => getStyles(components, core), [components, core]);
    const useStyles = useMemo(() => createUseStyles(themedStyles, prefixClassName ? { name: prefixClassName } : undefined), [themedStyles]);
    const classes = useStyles() || {};
    return (
      React.createElement(WrappedComponent, {
        ...props,
        classes,
        themedStyles: useRawStyles ? themedStyles : undefined,
      } as P)
    );
  };

  HOC.displayName = `withThemedStyles(${componentName || getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
}


export const withUAPLThemedStyles = <P extends object>(
  WrappedComponent: RenderableComponent<P>,
  getStyles: (components: object, core: object) => Styles,
  options: WithThemedStylesOptions
) => withThemedStyles(WrappedComponent, getStyles, { ...options, prefixClassName: 'UAPL' });
